// All times in the app are shown in Pacific time.
//
// The API sends UTC timestamps without a timezone marker
// ("2026-10-02T17:45:00"), which browsers read as *local* time - so every time
// on the site showed the UTC clock (a 10:45 AM start displayed as 5:45 PM).
// parseServerDate treats unmarked timestamps as UTC, and the formatters
// convert to America/Los_Angeles (PST/PDT handled automatically).

export const APP_TIME_ZONE = 'America/Los_Angeles'

type DateInput = string | number | Date | null | undefined

export const parseServerDate = (value: DateInput): Date | null => {
  if (value === null || value === undefined || value === '') return null
  if (value instanceof Date) return value
  if (typeof value === 'number') return new Date(value)

  const hasTimeZone = /(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(value)
  // Date-only strings ("2026-10-02") are already parsed as UTC by browsers
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value)
  const date = new Date(hasTimeZone || isDateOnly ? value : `${value}Z`)
  return isNaN(date.getTime()) ? null : date
}

const format = (value: DateInput, options: Intl.DateTimeFormatOptions, fallback: string) => {
  const date = parseServerDate(value)
  if (!date) return fallback
  return date.toLocaleString('en-US', { timeZone: APP_TIME_ZONE, ...options })
}

/** "Oct 2, 2026, 10:45 AM PDT" */
export const formatDateTime = (value: DateInput, fallback = '-') =>
  format(
    value,
    {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short',
    },
    fallback
  )

/** "10/2/2026" */
export const formatDate = (value: DateInput, fallback = '-') =>
  format(value, { year: 'numeric', month: 'numeric', day: 'numeric' }, fallback)

/** "10:45:12" (24h, for log lines) */
export const formatTime = (value: DateInput, fallback = '--:--:--') =>
  format(
    value,
    { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false },
    fallback
  )
